import { Button } from "@super-resume-new/ui/components/button";
import { Group } from "@super-resume-new/ui/components/group";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@super-resume-new/ui/components/input-group";
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
} from "@super-resume-new/ui/components/select";
import { Bookmark, DollarSign, Flame, Search, X } from "lucide-react";
import { useEffect, useState } from "react";

export interface JobSearchFilters {
  query: string;
  country: string;
  workMode: "" | "remote" | "hybrid" | "onsite";
  seniority: "" | "intern" | "junior" | "mid" | "senior" | "staff" | "principal" | "manager";
  hasSalary: boolean;
  postedWithinDays: number | "";
  saved?: boolean;
}

export interface JobSearchBarProps {
  filters: JobSearchFilters;
  onChange: (newFilters: JobSearchFilters) => void;
}

export interface CountryItem {
  value: string;
  label: string;
}

const COUNTRY_ITEMS: CountryItem[] = [
  { value: "", label: "🌐 All Locations" },
  { value: "US", label: "🇺🇸 United States (US)" },
  { value: "IN", label: "🇮🇳 India (IN)" },
  { value: "GB", label: "🇬🇧 United Kingdom (GB)" },
  { value: "CA", label: "🇨🇦 Canada (CA)" },
  { value: "DE", label: "🇩🇪 Germany (DE)" },
  { value: "AU", label: "🇦🇺 Australia (AU)" },
  { value: "SG", label: "🇸🇬 Singapore (SG)" },
  { value: "IE", label: "🇮🇪 Ireland (IE)" },
  { value: "NL", label: "🇳🇱 Netherlands (NL)" },
  { value: "FR", label: "🇫🇷 France (FR)" },
  { value: "JP", label: "🇯🇵 Japan (JP)" },
  { value: "CH", label: "🇨🇭 Switzerland (CH)" },
  { value: "SE", label: "🇸🇪 Sweden (SE)" },
  { value: "ES", label: "🇪🇸 Spain (ES)" },
  { value: "PL", label: "🇵🇱 Poland (PL)" },
  { value: "BR", label: "🇧🇷 Brazil (BR)" },
  { value: "IL", label: "🇮🇱 Israel (IL)" },
  { value: "AE", label: "🇦🇪 United Arab Emirates (AE)" },
  { value: "NZ", label: "🇳🇿 New Zealand (NZ)" },
  { value: "IT", label: "🇮🇹 Italy (IT)" },
  { value: "AT", label: "🇦🇹 Austria (AT)" },
  { value: "DK", label: "🇩🇰 Denmark (DK)" },
  { value: "NO", label: "🇳🇴 Norway (NO)" },
  { value: "FI", label: "🇫🇮 Finland (FI)" },
  { value: "PT", label: "🇵🇹 Portugal (PT)" },
  { value: "EE", label: "🇪🇪 Estonia (EE)" },
  { value: "CZ", label: "🇨🇿 Czech Republic (CZ)" },
  { value: "RO", label: "🇷🇴 Romania (RO)" },
  { value: "MX", label: "🇲🇽 Mexico (MX)" },
  { value: "AR", label: "🇦🇷 Argentina (AR)" },
  { value: "CO", label: "🇨🇴 Colombia (CO)" },
  { value: "CL", label: "🇨🇱 Chile (CL)" },
  { value: "ZA", label: "🇿🇦 South Africa (ZA)" },
  { value: "NG", label: "🇳🇬 Nigeria (NG)" },
  { value: "KE", label: "🇰🇪 Kenya (KE)" },
  { value: "EG", label: "🇪🇬 Egypt (EG)" },
  { value: "KR", label: "🇰🇷 South Korea (KR)" },
  { value: "TW", label: "🇹🇼 Taiwan (TW)" },
  { value: "HK", label: "🇭🇰 Hong Kong (HK)" },
  { value: "MY", label: "🇲🇾 Malaysia (MY)" },
  { value: "ID", label: "🇮🇩 Indonesia (ID)" },
  { value: "PH", label: "🇵🇭 Philippines (PH)" },
  { value: "VN", label: "🇻🇳 Vietnam (VN)" },
  { value: "TH", label: "🇹🇭 Thailand (TH)" },
  { value: "TR", label: "🇹🇷 Turkey (TR)" },
  { value: "GR", label: "🇬🇷 Greece (GR)" },
  { value: "BE", label: "🇧🇪 Belgium (BE)" },
];

const WORK_MODES: Array<{ value: "" | "remote" | "hybrid" | "onsite"; label: string }> = [
  { value: "", label: "All" },
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" },
  { value: "onsite", label: "Onsite" },
];

const SENIORITIES: Array<{
  value: "" | "intern" | "junior" | "mid" | "senior" | "staff" | "principal" | "manager";
  label: string;
}> = [
  { value: "", label: "All" },
  { value: "intern", label: "Intern" },
  { value: "junior", label: "Junior" },
  { value: "mid", label: "Mid" },
  { value: "senior", label: "Senior" },
  { value: "staff", label: "Staff+" },
];

const RECENCY_OPTIONS: Array<{ value: "" | 1 | 3 | 7; label: string }> = [
  { value: "", label: "Anytime" },
  { value: 1, label: "Past 24h" },
  { value: 3, label: "Past 3d" },
  { value: 7, label: "Past 7d" },
];

export function JobSearchBar({ filters, onChange }: JobSearchBarProps) {
  const update = (partial: Partial<JobSearchFilters>) => {
    onChange({ ...filters, ...partial });
  };

  // The keyword box is uncontrolled between submits. Typing must not hit the
  // network: the query is committed on Enter, on blur, or on clear.
  const [draft, setDraft] = useState(filters.query);
  useEffect(() => setDraft(filters.query), [filters.query]);

  const submitQuery = (value: string) => {
    if (value.trim() === filters.query) return;
    update({ query: value.trim() });
  };

  const selectedCountryItem =
    COUNTRY_ITEMS.find((c) => c.value.toUpperCase() === filters.country.toUpperCase()) ??
    COUNTRY_ITEMS[0];
  const countryFlag = selectedCountryItem.label.split(" ")[0] || "🌐";

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5 shadow-sm">
      {/* Search Input & Embedded Country Combobox Group */}
      {/* Left: Job Keyword Input */}
      <InputGroup>
        <InputGroupAddon align="inline-start">
          <Search className="size-4 text-muted-foreground" />
        </InputGroupAddon>
        <InputGroupInput
          type="search"
          placeholder="Search job title, keywords, or company — press Enter"
          value={draft}
          onChange={(e) => setDraft((e.target as HTMLInputElement).value)}
          onBlur={() => submitQuery(draft)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submitQuery(draft);
            }
            if (e.key === "Escape") setDraft(filters.query);
          }}
          className="w-full text-sm font-normal"
        />
        {draft && (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={() => {
              setDraft("");
              submitQuery("");
            }}
          >
            <X className="size-3.5" />
            <span className="sr-only">Clear search</span>
          </Button>
        )}
        <Select
          value={filters.country}
          onValueChange={(val) => update({ country: (val as string) ?? "" })}
        >
          <SelectTrigger
            title={`Location: ${selectedCountryItem.label}`}
            aria-label={`Select country location, currently: ${selectedCountryItem.label}`}
            className="min-w-0 w-auto sm:w-56 sm:min-w-56 border-0 border-l border-input rounded-l-none shadow-none px-2.5 sm:px-3.5 ml-auto text-sm focus-visible:ring-0 shrink-0 justify-center sm:justify-between [&_[data-slot=select-icon]]:hidden sm:[&_[data-slot=select-icon]]:flex"
          >
            <span className="sm:hidden text-base leading-none select-none">{countryFlag}</span>
            <span className="hidden sm:inline-block truncate text-sm font-normal text-foreground">
              {selectedCountryItem.label}
            </span>
          </SelectTrigger>
          <SelectPopup align="end" className="w-72 max-w-[calc(100vw-2rem)] max-h-80">
            {COUNTRY_ITEMS.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectPopup>
        </Select>
      </InputGroup>

      {/* Filter Row: Mode, Seniority, Recency & Salary */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 pt-1">
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          {/* Work Mode Toggle Group */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-muted-foreground shrink-0">Mode:</span>
            <Group className="shadow-sm">
              {WORK_MODES.map((mode) => {
                const isActive = filters.workMode === mode.value;
                return (
                  <Button
                    key={mode.value}
                    type="button"
                    size="sm"
                    variant={isActive ? "default" : "outline"}
                    onClick={() => update({ workMode: mode.value })}
                    className={`text-xs h-8 px-2.5 cursor-pointer transition ${
                      isActive
                        ? "bg-primary hover:bg-primary/90 text-white font-semibold shadow-sm"
                        : "text-muted-foreground hover:text-foreground bg-background"
                    }`}
                  >
                    {mode.label}
                  </Button>
                );
              })}
            </Group>
          </div>

          {/* Seniority Toggle Group (Includes Intern!) */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-muted-foreground shrink-0">Level:</span>
            <Group className="shadow-sm">
              {SENIORITIES.map((lvl) => {
                const isActive = filters.seniority === lvl.value;
                return (
                  <Button
                    key={lvl.value}
                    type="button"
                    size="sm"
                    variant={isActive ? "default" : "outline"}
                    onClick={() => update({ seniority: lvl.value })}
                    className={`text-xs h-8 px-2.5 cursor-pointer transition ${
                      isActive
                        ? "bg-primary hover:bg-primary/90 text-white font-semibold shadow-sm"
                        : "text-muted-foreground hover:text-foreground bg-background"
                    }`}
                  >
                    {lvl.label}
                  </Button>
                );
              })}
            </Group>
          </div>
        </div>

        {/* Right Side: Recency & Stated Salary */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Recency / Freshness Toggle */}
          <div className="flex items-center gap-1">
            <Group className="shadow-sm">
              {RECENCY_OPTIONS.map((rec) => {
                const isActive = filters.postedWithinDays === rec.value;
                return (
                  <Button
                    key={rec.label}
                    type="button"
                    size="sm"
                    variant={isActive ? "default" : "outline"}
                    onClick={() => update({ postedWithinDays: rec.value })}
                    title={`Jobs posted ${rec.label.toLowerCase()}`}
                    className={`text-xs h-8 px-2 cursor-pointer transition ${
                      isActive
                        ? "bg-primary hover:bg-primary/90 text-white font-semibold shadow-sm"
                        : "text-muted-foreground hover:text-foreground bg-background"
                    }`}
                  >
                    {rec.value === 1 && (
                      <Flame className="size-3 text-amber-700 dark:text-amber-400 mr-1 inline" />
                    )}
                    {rec.label}
                  </Button>
                );
              })}
            </Group>
          </div>

          {/* Stated Salary Button */}
          <Button
            type="button"
            size="sm"
            variant={filters.hasSalary ? "default" : "outline"}
            onClick={() => update({ hasSalary: !filters.hasSalary })}
            className={`text-xs h-8 px-2.5 cursor-pointer transition ${
              filters.hasSalary
                ? "font-semibold border-emerald-600 shadow-sm"
                : "text-muted-foreground hover:text-foreground bg-background"
            }`}
          >
            <DollarSign className="size-3.5 mr-1" />
            Salary
          </Button>

          {/* Saved Jobs Filter Button */}
          <Button
            type="button"
            size="sm"
            variant={filters.saved ? "default" : "outline"}
            onClick={() => update({ saved: !filters.saved })}
            className={`text-xs h-8 px-2.5 cursor-pointer transition ${
              filters.saved
                ? "bg-amber-600 hover:bg-amber-700 text-white font-semibold border-amber-600 shadow-sm"
                : "text-muted-foreground hover:text-foreground bg-background"
            }`}
          >
            <Bookmark className={`size-3.5 mr-1 ${filters.saved ? "fill-white" : ""}`} />
            Saved
          </Button>
        </div>
      </div>
    </div>
  );
}
